import { useMemo, useState } from 'react';

import { useQuery } from '@tanstack/react-query';

import { ExpandableJobCard } from '@/components/features/ExpandableJobCard';
import { JOB_QUERY_KEY, JOB_QUERY_OPTIONS, JobIcon } from '@/constant';
import { jobsApi } from '@/services/jobs';
import { JOB_STATUS } from '@/types/jobs';
import { AutocompleteCandidate } from '@/types/search';
import { getJobStatusCategoryColors, getJobStatusLabel } from '@/utils/job';
import {
  getJobCardDateLabel,
  getJobCardEngineerLabel,
} from '@/utils/jobCardStatusLine';
import { getJobSeeMoreFromVh3Ai } from '@/utils/searchOutcomes';

import { OutcomeExpandedDetails } from './OutcomeExpandedDetails';

type AutocompleteJobCardProps = {
  candidate: AutocompleteCandidate;
  onOpenJob: () => void;
};

const isJobStatus = (value: string): value is JOB_STATUS => {
  return Object.values(JOB_STATUS).includes(value as JOB_STATUS);
};

const extraString = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
};

export const AutocompleteJobCard = ({
  candidate,
  onOpenJob,
}: AutocompleteJobCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const jobId = String(candidate.id);
  const extra = candidate.extra;
  const { data: job } = useQuery({
    queryKey: [JOB_QUERY_KEY, jobId],
    queryFn: ({ signal }) => jobsApi.getJob(jobId, signal),
    ...JOB_QUERY_OPTIONS,
    enabled: Boolean(jobId) && isExpanded,
  });

  const rawStatus = extraString(extra?.status) ?? job?.status ?? null;
  const statusLabel = rawStatus
    ? isJobStatus(rawStatus)
      ? getJobStatusLabel(rawStatus)
      : rawStatus
    : null;
  const statusColor =
    rawStatus && isJobStatus(rawStatus)
      ? getJobStatusCategoryColors(rawStatus).color
      : 'tags.blue.font';

  const dateLabel =
    getJobCardDateLabel({
      actualEndAt: job?.actualEndAt,
      actualStartAt: job?.actualStartAt,
      plannedStartAt: job?.plannedStartAt,
    }) ??
    getJobCardDateLabel({
      actualEndAt: extraString(extra?.actualEndAt),
      actualStartAt: extraString(extra?.actualStartAt),
      plannedStartAt: extraString(extra?.plannedStartAt),
    });

  const engineerName =
    getJobCardEngineerLabel(job?.resourceName) ??
    getJobCardEngineerLabel(extraString(extra?.resourceName));

  const seeMore = useMemo(
    () => getJobSeeMoreFromVh3Ai(job?.vh3_ai),
    [job?.vh3_ai]
  );

  return (
    <ExpandableJobCard
      title={candidate.label}
      sublabel={candidate.sublabel}
      statusLabel={statusLabel}
      statusColor={statusColor}
      dateLabel={dateLabel}
      engineerName={engineerName}
      summary={isExpanded ? seeMore.summary : null}
      icon={<JobIcon size={16} />}
      isExpanded={isExpanded}
      onToggleExpand={() => setIsExpanded(open => !open)}
      onOpenJob={onOpenJob}
      openJobAriaLabel={`Open job ${candidate.label}`}
      data-cy='global-search-result'
      showExpandHint={Boolean(seeMore.summary || seeMore.hasDetails)}
    >
      {seeMore.hasDetails ? (
        <OutcomeExpandedDetails
          technicalDetails={seeMore.technicalDetails}
          stepsBreakdown={seeMore.stepsBreakdown}
          summary={seeMore.summary}
          fullText={seeMore.fullText}
        />
      ) : null}
    </ExpandableJobCard>
  );
};
